import { useEffect, useState } from 'react';
import { Button, Flex, Form, Input, Modal, Switch } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  PlusBackground,
  Delete,
  ArrowDropdownSmallWithoutBg,
} from '@shared/assets/icons/components';
import styles from './field-create.module.scss';
import { useFormSchema } from '../hooks/use-form-schema';
import type { CreateAgentFieldValues } from '../lib/types';
import { AGENT_FIELD_FORM_LIMITS } from '../lib/constants';
import { useCreateField } from '../hooks/use-create-field';
import { useUpdateField } from '../hooks/use-update-field';
import { Select, Text } from '@shared/ui/antd';
import type { AgentField } from '@shared/api';

const ALIAS_OPTIONS = [
  'name',
  'phone',
  'url',
  'yandex_client_id',
  'yandex_counter_id',
  'matomo_uid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_group',
  'utm_term',
  'utm_gen',
].map((value) => ({ value, label: value }));

interface FieldCreateModalProps {
  open: boolean;
  agentId: number;
  onClose: () => void;
  selectedField?: AgentField | null;
  onSuccess?: () => void;
}

export const FieldCreateModal = ({
  open,
  agentId,
  onClose,
  selectedField,
  onSuccess,
}: FieldCreateModalProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.fields.create' });
  const formSchema = useFormSchema();
  const { createField, isLoading } = useCreateField();
  const { updateField, isLoading: isUpdating } = useUpdateField();

  const title = selectedField ? t('updateTitle') : t('title');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isAliasDropdownOpen, setIsAliasDropdownOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<CreateAgentFieldValues>({
    mode: 'onTouched',
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: selectedField?.name ?? '',
      description: selectedField?.description ?? '',
      filter: selectedField?.filter?.length ? selectedField.filter : [''],
      alias: selectedField?.alias ?? '',
      useKnowledgeFilter: selectedField?.use_knowledge_filter ?? false,
      isRequired: selectedField?.is_required ?? false,
      isActive: selectedField?.is_active ?? true,
    },
  });

  const filterValues = watch('filter') ?? [];
  const safeFilterValues = filterValues.length ? filterValues : [''];

  useEffect(() => {
    if (open) {
      reset({
        name: selectedField?.name ?? '',
        description: selectedField?.description ?? '',
        filter: selectedField?.filter?.length ? selectedField.filter : [''],
        alias: selectedField?.alias ?? '',
        useKnowledgeFilter: selectedField?.use_knowledge_filter ?? false,
        isRequired: selectedField?.is_required ?? false,
        isActive: selectedField?.is_active ?? true,
      });
      setIsAdvancedOpen(false);
      setIsAliasDropdownOpen(false);
    }
  }, [open, reset, selectedField]);

  const handleAddValue = () => {
    const current = getValues('filter') ?? [];
    if (current.length < AGENT_FIELD_FORM_LIMITS.filterMax) {
      const next = [...current, ''];
      setValue('filter', next, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleRemoveValue = (index: number) => {
    const current = getValues('filter') ?? [];
    if (current.length <= 1) return;
    const next = [...current];
    next.splice(index, 1);
    if (!next.length) next.push('');
    setValue('filter', next, { shouldValidate: true, shouldDirty: true });
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: CreateAgentFieldValues) => {
    if (selectedField) {
      await updateField(selectedField.id, values);
      onSuccess?.();
      reset();
      onClose();
      return;
    }

    await createField(agentId, values);
    onSuccess?.();
    reset();
    onClose();
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      width={640}
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ hidden: true }}
    >
      <Flex vertical gap={16} style={{ padding: '8px 4px' }}>
        <Flex>
          <Text variant="3xl" fontWeight={700} className={styles.title}>
            {title}
          </Text>
        </Flex>

        <Form
          onChange={() => clearErrors('root')}
          onFinish={handleSubmit(onSubmit)}
          className={styles.root}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label={t('name')}
            help={errors.name?.message}
            validateStatus={errors.name ? 'error' : ''}
          >
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  {...field}
                  showCount
                  maxLength={AGENT_FIELD_FORM_LIMITS.name}
                  placeholder={t('namePlaceholder')}
                  className={styles.input}
                  disabled={isSubmitting}
                />
              )}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('description')}
            help={errors.description?.message}
            validateStatus={errors.description ? 'error' : ''}
          >
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  showCount
                  maxLength={AGENT_FIELD_FORM_LIMITS.description}
                  placeholder={t('descriptionPlaceholder')}
                  className={styles.textArea}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  disabled={isSubmitting}
                />
              )}
            />
          </Form.Item>

          <Flex vertical gap={8} className={styles.section}>
            <Flex className={styles.labelRow} justify="space-between">
              <Flex className={styles.labelRow} gap={8} align="center">
                <Text variant="md" fontWeight={700} className={styles.sectionTitle}>
                  {t('isActive')}
                </Text>
              </Flex>

              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <Switch checked={field.value} onChange={(checked) => field.onChange(checked)} />
                )}
              />
            </Flex>
          </Flex>

          <Flex vertical gap={8} className={styles.section}>
            <Flex className={styles.labelRow} justify="space-between">
              <Flex className={styles.labelRow} gap={8} align="center">
                <Text variant="md" fontWeight={700} className={styles.sectionTitle}>
                  {t('requiredLabel')}
                </Text>
              </Flex>

              <Controller
                control={control}
                name="isRequired"
                render={({ field }) => (
                  <Switch checked={field.value} onChange={(checked) => field.onChange(checked)} />
                )}
              />
            </Flex>

            <Text variant="md" fontWeight={500} className={styles.helper}>
              {t('requiredDescription')}
            </Text>
          </Flex>

          <Flex vertical gap={8} className={styles.section}>
            <button
              type="button"
              className={styles.advancedHeader}
              onClick={() => setIsAdvancedOpen((prev) => !prev)}
            >
              <Text variant="md" fontWeight={700} className={styles.sectionTitle}>
                {t('advancedTitle')}
              </Text>
              <ArrowDropdownSmallWithoutBg
                className={classNames(styles.advancedIcon, {
                  [styles.advancedIcon_open]: isAdvancedOpen,
                })}
                color="#A7A8AE"
              />
            </button>

            {isAdvancedOpen && (
              <Flex vertical gap={12} className={styles.advancedContent}>
                <Form.Item
                  name="alias"
                  label={t('aliasLabel')}
                  help={errors.alias?.message}
                  validateStatus={errors.alias ? 'error' : ''}
                >
                  <Controller
                    control={control}
                    name="alias"
                    render={({ field }) => {
                      const value = field.value ? [field.value] : [];

                      return (
                        <Select
                          value={value}
                          options={ALIAS_OPTIONS}
                          placeholder={t('aliasPlaceholder')}
                          className={styles.select}
                          selectMode="tags"
                          isSingleSelectMode
                          allowClear
                          hasSearch
                          isDisabled={isSubmitting}
                          open={isAliasDropdownOpen}
                          onDropdownVisibleChange={setIsAliasDropdownOpen}
                          onChange={(next) => {
                            const nextValue = Array.isArray(next) ? next.at(-1) : next;
                            field.onChange(nextValue ?? '');
                            setIsAliasDropdownOpen(false);
                          }}
                        />
                      );
                    }}
                  />
                </Form.Item>

                {selectedField && (
                  <Flex className={styles.labelRow} justify="space-between">
                    <Text variant="md" fontWeight={600}>
                      {t('useFilterLabel')}
                    </Text>

                    <Controller
                      control={control}
                      name="useKnowledgeFilter"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(checked) => field.onChange(checked)}
                        />
                      )}
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>

          <Flex vertical gap={12} className={styles.section}>
            <Flex align="center" justify="space-between">
              <Flex gap={8} align="center">
                <span className={styles.sectionTitle}>{t('valuesLabel')}</span>
                <span className={styles.helper}>{t('valuesOptional')}</span>
              </Flex>
            </Flex>

            {safeFilterValues.map((_, index) => (
              <Flex key={index} className={styles.valueRow}>
                <Form.Item
                  name={`filter.${index}`}
                  help={errors.filter?.[index]?.message}
                  validateStatus={errors.filter?.[index] ? 'error' : ''}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Controller
                    control={control}
                    name={`filter.${index}`}
                    render={({ field }) => (
                      <Input.TextArea
                        {...field}
                        placeholder={t('valuePlaceholder')}
                        className={styles.textArea}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        showCount
                        maxLength={AGENT_FIELD_FORM_LIMITS.filter}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </Form.Item>

                <Button
                  type="text"
                  icon={<Delete color="#ED131C" width={20} height={20} />}
                  className={styles.deleteButton}
                  onClick={() => handleRemoveValue(index)}
                  disabled={safeFilterValues.length <= 1 || isSubmitting}
                />
              </Flex>
            ))}

            <Button
              icon={<PlusBackground fill="var(--color-text-secondary)" />}
              iconPosition="end"
              type="text"
              className={styles.addButton}
              onClick={handleAddValue}
              disabled={
                safeFilterValues.length >= AGENT_FIELD_FORM_LIMITS.filterMax || isSubmitting
              }
            >
              {t('addValue')}
            </Button>
          </Flex>

          <Flex gap="12px" align="center" justify="end" className={styles.button_container}>
            <Button
              variant="text"
              onClick={handleCancel}
              className={classNames(styles.button, styles.button_cancel)}
              disabled={isSubmitting || isLoading || isUpdating}
            >
              {t('cancel')}
            </Button>

            <Form.Item className={styles.button}>
              <Button
                className={styles.button}
                block
                type="primary"
                htmlType="submit"
                loading={isSubmitting || isLoading || isUpdating}
                disabled={!isValid || isSubmitting || isLoading || isUpdating || !isDirty}
              >
                {selectedField ? t('update') : t('submit')}
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
};
